import { useMemo, useState } from "react";
import { Paper, Title, Box, Text, Group, Stack, UnstyledButton, Progress } from "@mantine/core";
import { IconChevronDown, IconChevronRight } from "@tabler/icons-react";
import { flagEmoji, rollupCities, rollupCountries } from "./trafficDisplay";
import rowStyles from "./inspectRow.module.css";

/**
 * Where sessions arrived from, geographically.
 *
 * Country and city are looked up from the visitor's IP at ingest (MaxMind) and stored on the
 * event; the IP itself is discarded. First-touch, like the rest of this page: the location of
 * the first pageview of the session, not of every subsequent click.
 */
export default function TrafficLocations({ countries, cities, totalSessions, isLoading }) {
  const [openCountry, setOpenCountry] = useState(null);

  const rows = useMemo(() => rollupCountries(countries), [countries]);
  const located = useMemo(
    () => rows.reduce((sum, r) => sum + r.sessions, 0),
    [rows],
  );

  if (isLoading) {
    return (
      <Paper p="md" withBorder>
        <Title order={4}>Locations</Title>
        <Box h={250} style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Text c="dimmed">Loading...</Text>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper p="md" withBorder>
      <Title order={4}>Locations</Title>
      <Text size="xs" c="dimmed" mb="md">
        Country and city of the first pageview. Click a country to see cities.
      </Text>

      {rows.length === 0 ? (
        <Box h={200} style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Text c="dimmed" ta="center" maw={360}>
            No location data yet. Location is looked up from the visitor&apos;s IP when the event
            is recorded, and the IP is not stored.
          </Text>
        </Box>
      ) : (
        <Stack gap={2}>
          {rows.map((row) => {
            const opened = openCountry === row.code;
            const cityRows = opened ? rollupCities(cities, row.code) : [];
            const pct = located > 0 ? (row.sessions / located) * 100 : 0;
            return (
              <div key={row.code || row.name}>
                <UnstyledButton
                  onClick={() => setOpenCountry(opened ? null : row.code)}
                  w="100%"
                  className={rowStyles.row}
                  aria-expanded={opened}
                  aria-label={`${row.name}, ${row.sessions} sessions`}
                >
                  <Group gap="sm" wrap="nowrap" py={8} px={4}>
                    {opened ? (
                      <IconChevronDown size={14} color="var(--mantine-color-dimmed)" />
                    ) : (
                      <IconChevronRight size={14} color="var(--mantine-color-dimmed)" />
                    )}
                    <Text size="sm" style={{ width: 28, flexShrink: 0 }} ta="center">
                      {flagEmoji(row.code)}
                    </Text>
                    <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                      <Group justify="space-between" wrap="nowrap">
                        <Text size="sm" truncate>
                          {row.name}
                        </Text>
                        <Text size="sm" style={{ fontVariantNumeric: "tabular-nums" }}>
                          {row.sessions.toLocaleString()}
                        </Text>
                      </Group>
                      <Progress value={pct} size="xs" color="teal.6" radius="xs" />
                    </Stack>
                    <Text size="xs" c="dimmed" w={40} ta="right">
                      {pct.toFixed(0)}%
                    </Text>
                  </Group>
                </UnstyledButton>
                {opened && (
                  <Stack gap={2} pb="sm" pl={44}>
                    {cityRows.length === 0 ? (
                      <Text size="xs" c="dimmed" py={4}>
                        No city was recorded for these sessions.
                      </Text>
                    ) : (
                      cityRows.map((city) => {
                        const cityPct = row.sessions > 0 ? (city.sessions / row.sessions) * 100 : 0;
                        return (
                          <Group
                            key={city.city}
                            gap="sm"
                            wrap="nowrap"
                            py={4}
                            justify="space-between"
                          >
                            <Text size="sm">{city.city}</Text>
                            <Group gap="sm" wrap="nowrap">
                              <Progress
                                value={cityPct}
                                size="xs"
                                color="gray.5"
                                w={80}
                                radius="xs"
                              />
                              <Text size="sm" w={56} ta="right" style={{ fontVariantNumeric: "tabular-nums" }}>
                                {city.sessions.toLocaleString()}
                              </Text>
                            </Group>
                          </Group>
                        );
                      })
                    )}
                  </Stack>
                )}
              </div>
            );
          })}
        </Stack>
      )}

      {located > 0 && totalSessions > located && (
        <Text size="xs" c="dimmed" mt="md">
          Located {located.toLocaleString()} of {totalSessions.toLocaleString()} sessions. The
          rest had no geo lookup.
        </Text>
      )}
    </Paper>
  );
}
